import { Injectable } from '@angular/core';
import { OvAutoService } from '@ov-suite/services';
import { OrderModel, OrderItemModel } from '@ov-suite/models-order';
import { BehaviorSubject } from 'rxjs';
import jsPDF from 'jspdf';
import autoTable, { RowInput, Styles } from 'jspdf-autotable';
import moment from 'moment';
import { AuthenticationService } from '@ov-suite/authguard-angular/lib/authentication/authentication.service';

/**
 * Section 1: Order number and basic Customer information.
 * Section 2: Invoicing from and to information.
 * Section 3: Invoice items information.
 * Section 4: Truck driver, license plate and load detail information.
 * Section 5: Banking, total, and signage position information.
 */

const IMAGE_WIDTH = 120;
const IMAGE_HEIGHT = 40;

interface InvoiceItem {
  itemCode?: string;
  itemName?: string;
  ordered?: number;
  quantity?: number;
  priceExl?: string;
  priceInc?: string;
  vatRate?: string;
  tax?: string;
  totalExcl?: string;
  totalIncl?: string;
  note?: string;
}

interface GrandTotals {
  runningTotalIncl: string;
  runningTotalExcl: string;
  runningTotalTax: string;
}

interface InvoiceItemTotals {
  orderInvoiceItems: (string | number)[][];
  grandTotal: GrandTotals;
}

interface OrderInfo {
  order: OrderModel;
  orderCode?: string;
  externalOrderCode?: string;
  commitDate?: string;
  invoiceNum?: string;
  customerCode?: string;
  customerName?: string;
  customerTaxNumber?: string;
  terms?: string;
  address?: AddressInfo;
  deliveryAddress?: AddressInfo;
}

interface AddressInfo {
  line1?: string;
  line2?: string;
  line3?: string;
  line4?: string;
  postalCode?: string;
}

class PDFPageParams {
  constructor(public readonly width: number, public readonly height: number, public readonly pageMargin: number) {}

  public get pageMiddle() {
    return this.width / 2;
  }

  public get pageOneThird() {
    return this.width / 3;
  }

  public get pageOneQuarter() {
    return this.width / 4;
  }

  public get pageTwoThird() {
    return (this.width / 3) * 2;
  }

  public get pageThreeQuarter() {
    return (this.width / 4) * 3;
  }

  public get pageStartTop() {
    return this.pageMargin;
  }

  public get pageFifth() {
    return this.width / 5;
  }

  public get pageSixth() {
    return this.width / 6;
  }
}

interface VehicleInfo {
  driver?: string;
  registration?: string;
}

interface AgentDetails {
  createdByAgent?: string;
  modifiedByAgent?: string;
}

interface DetailInfo {
  title?: string;
  value?: string;
}

const trudaPmbInfo = {
  name: 'Truda Foods (Pty) Ltd - PMB',
  addressLine1: 'Lester Brown Rd',
  addressLine2: 'Mkondeni',
  addressLine3: 'Pietermaritzburg',
  addressLine4: 'Kwazulu Natal',
  addressLine5: '',
  fax: '033 346 2935',
  email: 'TBD',
  website: 'www.trudafoods.co.za',
  taxRegistration: '455 021 5687',
  telephone: '033 346 1236',
  registration: '2013/036303/07',
};

const invoiceBatchSize = 24;

@Injectable({ providedIn: 'root' })
export class ProformaInvoiceDownloadService {
  invoices: BehaviorSubject<OrderModel[]> = new BehaviorSubject<OrderModel[]>(null);

  loadId: number;

  orderId: number;

  doc: jsPDF;
  pageParams: PDFPageParams;

  constructor(private readonly ovAutoService: OvAutoService, private readonly authenticationService: AuthenticationService) {}

  prepareOrderInfo(order: OrderModel): OrderInfo {
    return {
      order,
      orderCode: order.orderCode,
      externalOrderCode: order.externalOrderNo,
      commitDate: moment(order.orderDate).format('YYYY-MM-DD'),
      invoiceNum: `INV-${moment().format('YYYYMMDD')}-${order.orderCode}`,
      customerCode: order.customer.customerCode,
      customerName: order.customer.name,
      customerTaxNumber: order.customer.taxNumber,
      terms: `${order.customer.accountReceivableComment2 || 'Net 30 days'}`,
      address: order.customer.postalAddress,
      deliveryAddress: order.deliveryAddress,
    };
  }

  async fetchOrderData(orderId: number) {
    return this.ovAutoService.get({
      entity: OrderModel,
      keys: [
        'id',
        'orderCode',
        'externalOrderNo',
        'orderDate',
        'fulfilmentDate',
        'specialInstructions',
        'notes',
        'customer.customerCode',
        'customer.name',
        'customer.taxNumber',
        'customer.accountReceivableComment1',
        'customer.accountReceivableComment2',
        'customer.postalAddress',
        'deliveryAddress',
        'deliveryAddress.line1',
        'deliveryAddress.line2',
        'deliveryAddress.line3',
        'deliveryAddress.line4',
        'deliveryAddress.line5',
        'deliveryAddress.line6',
        'deliveryAddress.postalCode',
        'orderItems',
        'orderItems.id',
        'orderItems.quantity',
        'orderItems.unitPriceExcl',
        'orderItems.unitPriceIncl',
        'orderItems.productSku.sku',
        'orderItems.productSku.name',
        'orderItems.note',
        'createdByUser.firstName',
        'createdByUser.lastName',
        'modifiedByUser.firstName',
        'modifiedByUser.lastName',
        'salesRep.name',
      ],
      id: orderId,
    });
  }

  private writePage(orderInfo: OrderInfo, orderItems: OrderItemModel[], agentDetails: AgentDetails) {
    this.populateDocTitle();
    const sectionOneEnd = this.populateInvoiceSectionOne(orderInfo);
    const sectionTwoEnd = this.populateInvoiceSectionTwo(sectionOneEnd, orderInfo);

    // Prepare invoice items data
    const invoiceItems = this.prepareInvoiceItems(orderInfo.order, orderItems);
    const formattedBatch = this.formatBatchNumbers(invoiceItems.orderInvoiceItems);
    const sectionThreeEnd = this.populateInvoiceSectionThree(sectionTwoEnd, formattedBatch);

    this.populateFooter(invoiceItems.grandTotal, agentDetails);
  }

  private getPageBatchOfOrderItems(orderItems: OrderItemModel[], batchSize: number): OrderItemModel[][] {
    const batches: OrderItemModel[][] = [];
    for (let i = 0; i < orderItems.length; i += batchSize) {
      batches.push(orderItems.slice(i, i + batchSize));
    }
    return batches;
  }

  async exportInvoice(orderId: number) {
    const order = await this.fetchOrderData(orderId);
    const orderInfo = this.prepareOrderInfo(order);

    this.pageParams = new PDFPageParams(600, 840, 20);
    this.doc = new jsPDF('p', 'px', [this.pageParams.width, this.pageParams.height]); // [x - 595.28, y - 841.89]

    // Prepare agent details
    const agentDetails: AgentDetails = {
      createdByAgent: order.createdByUser ? order.createdByUser.firstName + ' ' + order.createdByUser.lastName : '',
      modifiedByAgent: order.modifiedByUser ? order.modifiedByUser.firstName + ' ' + order.modifiedByUser.lastName : '',
    };

    const pages = this.getPageBatchOfOrderItems(order.orderItems, invoiceBatchSize);

    pages.forEach((page, index) => {
      this.writePage(orderInfo, page, agentDetails);
      if (index < pages.length - 1) {
        this.doc.addPage();
      }
    });

    // this.populateDocTitle();
    // const sectionOneEnd = this.populateInvoiceSectionOne(orderInfo);
    // const sectionTwoEnd = this.populateInvoiceSectionTwo(sectionOneEnd, orderInfo);
    //
    // // Prepare invoice items data
    // const invoiceItems = this.prepareInvoiceItems(order.orderItems);
    // const formattedBatch = this.formatBatchNumbers(invoiceItems.orderInvoiceItems);
    // const sectionThreeEnd = this.populateInvoiceSectionThree(sectionTwoEnd, formattedBatch);
    //
    //
    //
    // Populate remaining sections
    // this.populateInvoiceSectionFive(sectionThreeEnd + 10, invoiceItems.grandTotal, agentDetails);

    this.addPageNumbers();

    // Generate a proper invoice number based on date and order code
    this.doc.save(`${order.orderCode}-${order.orderCode}.pdf`);
  }

  prepareInvoiceItems(order: OrderModel, orderItems: OrderItemModel[]): InvoiceItemTotals {
    let runningTotalExcl = 0;
    let runningTotalIncl = 0;
    let runningTotalTax = 0;

    const normalItems: (string | number)[][] = [];
    const specialItems: (string | number)[][] = [];
    const freeItems: (string | number)[][] = [];

    order.orderItems.forEach(item => {
      const quantity = item.quantity || 0;
      const unitPriceExcl = item.unitPriceExcl || 0;
      const unitPriceIncl = item.unitPriceIncl || 0;
      const totalExcl = quantity * unitPriceExcl;
      const totalIncl = quantity * unitPriceIncl;
      const tax = totalIncl - totalExcl;

      runningTotalExcl += totalExcl;
      runningTotalIncl += totalIncl;
      runningTotalTax += tax;
    });

    orderItems.forEach(item => {
      const quantity = item.quantity || 0;
      const unitPriceExcl = item.unitPriceExcl || 0;
      const unitPriceIncl = item.unitPriceIncl || 0;
      const totalExcl = quantity * unitPriceExcl;
      const totalIncl = quantity * unitPriceIncl;
      const tax = totalIncl - totalExcl;

      // Check if the item is free (price is zero)
      const isFree = unitPriceExcl === 0;

      if (isFree) {
        if (item.note) {
          freeItems.push([item.note]);
        }
        freeItems.push([item.productSku?.sku || '', item.productSku?.name || '', quantity, unitPriceExcl, tax, totalExcl]);
      } else if (!item.note) {
        normalItems.push([item.productSku?.sku || '', item.productSku?.name || '', quantity, unitPriceExcl, tax, totalExcl]);
      } else {
        specialItems.push([item.note], [item.productSku?.sku || '', item.productSku?.name || '', quantity, unitPriceExcl, tax, totalExcl]);
      }
    });

    // Add a "Free Stock" header if there are free items
    const freeItemsWithHeader = freeItems.length > 0 ? [['Free Stock']] : [];

    // Combine normal items, special items (with notes), and free items (with header)
    const orderInvoiceItems: (string | number)[][] = [...normalItems, ...specialItems, ...freeItemsWithHeader, ...freeItems];

    return {
      orderInvoiceItems,
      grandTotal: {
        runningTotalExcl: runningTotalExcl.toString(),
        runningTotalIncl: runningTotalIncl.toString(),
        runningTotalTax: runningTotalTax.toString(),
      },
    };
  }

  // This is used for getting comma separated numbers for totals in the invoice item section.
  formatBatchNumbers(batch: (string | number)[][]) {
    batch.forEach(b => {
      if (b.length === 6) {
        b[3] = this.formatNumber(b[3]);
        b[4] = this.formatNumber(b[4]);
        b[5] = this.formatNumber(b[5]);
      }
    });

    return batch;
  }

  populateDocTitle() {
    const { doc, pageParams } = this;
    autoTable(doc, {
      // head: [['', '']],
      showHead: false,
      margin: {
        left: 10,
        right: pageParams.pageOneQuarter,
      },
      columnStyles: {
        0: { fontStyle: 'bold', fontSize: 26, textColor: '#00008B' },
        1: { fontStyle: 'bold', fontSize: 16, halign: 'right', valign: 'bottom' },
      },
      bodyStyles: {
        cellPadding: 0.2,
      },
      startY: 10,
      theme: 'plain',
      body: [['Proforma Invoice', 'Truda Foods (Pty) Ltd - PMB']],
      // didDrawPage: data => {
      //   topSectionTableEndRight = data.cursor.y;
      // },
    });

    doc.setFont(undefined, 'normal');
    doc.setFontSize(12);
    doc.setTextColor('#000000');
  }

  getLongest(nums: number[]) {
    return nums.sort((a, b) => b - a)[0];
  }

  drawHorizontalLine(lineStart: number) {
    const { doc } = this;
    doc.line(10, lineStart, 590, lineStart);
  }

  populateInvoiceSectionOne(order: OrderInfo): number {
    const { doc, pageParams } = this;
    const bodyStyles: Partial<Styles> = {
      cellPadding: 0.2,
      fontSize: 14,
    };

    let topSectionTableEndLeft = 0;
    autoTable(doc, {
      head: [['', '']],
      margin: {
        left: 10,
        right: pageParams.pageMiddle + 40,
      },
      // tableWidth: pageParams.pageOneThird,
      startY: pageParams.pageStartTop,
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
      },
      bodyStyles,
      theme: 'plain',
      body: [
        ['Invoice No:', order.orderCode],
        ['Date:', moment().format('YYYY-MM-DD')],
        ['Your Account No:', order.customerCode],
        ['Your Ref:', ''],
      ],
      didDrawPage: data => {
        topSectionTableEndLeft = data.cursor.y;
      },
    });

    const email = this.authenticationService.user.getValue().email;
    let topSectionTableEndRight = 0;
    autoTable(doc, {
      head: [['', '']],
      margin: {
        left: pageParams.pageMiddle - 10,
        right: pageParams.pageOneQuarter,
      },
      // tableWidth: pageParams.pageOneThird,
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
      },
      bodyStyles,
      startY: pageParams.pageStartTop,
      theme: 'plain',
      body: [
        ['Reg No:', trudaPmbInfo.registration],
        ['Tel:', trudaPmbInfo.telephone],
        ['Fax:', trudaPmbInfo.fax],
        ['Email:', email],
        ['Website:', trudaPmbInfo.website],
      ],
      didDrawPage: data => {
        topSectionTableEndRight = data.cursor.y;
      },
    });

    const img = new Image();
    img.src = '/assets/img/truda-logo.png';
    doc.addImage(img, 'PNG', pageParams.pageThreeQuarter + 10, pageParams.pageMargin + 20, IMAGE_WIDTH, IMAGE_HEIGHT);

    const sectionOneLongest = this.getLongest([topSectionTableEndLeft, topSectionTableEndRight]);
    this.drawHorizontalLine(sectionOneLongest + 5);

    return sectionOneLongest;
  }

  populateInvoiceSectionTwo(startY: number, order: OrderInfo): number {
    const { doc, pageParams } = this;
    const sectionTwoStart = startY + 10;
    let sectionTwoTableStart = 0;
    let sectionTwoTableMiddle = 0;
    let sectionTwoTableEnd = 0;

    const cellPadding = 1;

    autoTable(doc, {
      margin: {
        left: 10,
        right: pageParams.pageTwoThird + 35,
      },
      columnStyles: {
        1: { halign: 'right' },
      },
      bodyStyles: {
        cellPadding,
      },
      startY: sectionTwoStart,
      theme: 'plain',
      body: [
        ['From:'],
        [trudaPmbInfo.name],
        [trudaPmbInfo.addressLine1],
        [trudaPmbInfo.addressLine2],
        [trudaPmbInfo.addressLine3],
        [trudaPmbInfo.addressLine4],
        [`Vat Registration: ${trudaPmbInfo.taxRegistration}`],
      ],
      willDrawCell: data => {
        switch (data.row.index) {
          case 0:
          case 1:
          case 6:
            doc.setFont(undefined, 'bold');
            break;
          default:
            doc.setFont(undefined, 'normal');
        }
      },
      didDrawPage: data => {
        sectionTwoTableStart = data.cursor.y;
      },
    });

    autoTable(doc, {
      margin: {
        left: pageParams.pageOneThird,
        right: pageParams.pageOneThird + 35,
      },
      columnStyles: {
        1: { halign: 'right' },
      },
      bodyStyles: {
        cellPadding,
      },
      startY: sectionTwoStart,
      theme: 'plain',
      body: [
        ['Invoice To:'],
        [order.customerName],
        [order?.address?.line1],
        [order?.address?.line2],
        [order?.address?.line3],
        [order?.address?.line4],
        [`Vat Registration: ${order?.customerTaxNumber}`],
      ],
      willDrawCell: data => {
        switch (data.row.index) {
          case 0:
          case 1:
          case 6:
            doc.setFont(undefined, 'bold');
            break;
          default:
            doc.setFont(undefined, 'normal');
        }
      },
      didDrawPage: data => {
        sectionTwoTableMiddle = data.cursor.y;
      },
    });

    autoTable(doc, {
      margin: {
        left: pageParams.pageTwoThird,
        right: 10,
      },
      columnStyles: {
        1: { halign: 'right' },
      },
      bodyStyles: {
        cellPadding,
      },
      startY: sectionTwoStart,
      theme: 'plain',
      body: [
        ['Deliver To:', ''],
        [order.customerName, ''],
        [order?.deliveryAddress?.line1, ''],
        [order?.deliveryAddress?.line2, ''],
        [order?.deliveryAddress?.line3, ''],
        [order?.deliveryAddress?.line4, ''],
      ],
      willDrawCell: data => {
        switch (data.row.index) {
          case 0:
          case 1:
            doc.setFont(undefined, 'bold');
            break;
          default:
            doc.setFont(undefined, 'normal');
        }
      },
      didDrawPage: data => {
        sectionTwoTableEnd = data.cursor.y;
      },
    });

    const sectionTwoLongest = this.getLongest([sectionTwoTableStart, sectionTwoTableEnd, sectionTwoTableMiddle]);
    this.drawHorizontalLine(sectionTwoLongest + 5);

    return sectionTwoLongest;
  }

  populateInvoiceSectionThree(startY: number, invoiceLines: (string | number)[][]) {
    const { doc, pageParams } = this;
    const sectionThreeStart = startY + 10;
    let invoiceItemLength = 0;

    autoTable(doc, {
      head: [['Item Code', 'Item Description', 'Quantity', 'Unit Price (Ex)', 'Tax', 'Total (Excl)']],
      headStyles: {
        fillColor: [238, 238, 238],
        halign: 'center',
        fontStyle: 'bold',
      },
      margin: {
        left: 10,
        right: 10,
      },
      startY: sectionThreeStart,
      theme: 'plain',
      body: invoiceLines,
      // Define column widths to ensure proper spacing
      columnStyles: {
        0: { halign: 'left', cellWidth: 100 }, // Item Code - left aligned
        1: { halign: 'left', cellWidth: 180 }, // Item Description - left aligned
        2: { halign: 'center', cellWidth: 60 }, // Quantity - center aligned
        3: { halign: 'right', cellWidth: 80 }, // Unit Price - right aligned
        4: { halign: 'right', cellWidth: 80 }, // Tax - right aligned
        5: { halign: 'right', cellWidth: 80 }, // Total - right aligned
      },
      didParseCell: data => {
        const rows = data.table.body;

        rows.forEach(row => {
          const rowRawData = row.raw as HTMLTableRowElement[];

          if (rowRawData.length === 1) {
            row.cells[0].styles.textColor = [0, 0, 255];
          }
        });
      },
      didDrawPage: data => {
        invoiceItemLength = data.cursor.y;
      },
    });

    return invoiceItemLength;
  }

  private populateFooter(totals: GrandTotals, agents: AgentDetails) {
    const startY = 600;
    const { doc, pageParams } = this;
    this.drawHorizontalLine(startY);

    let sectionFourTableStart = 0;
    let sectionFourTableMiddle = 0;
    let sectionFourTableEnd = 0;

    autoTable(doc, {
      head: [['', '']],
      margin: {
        left: 10,
        right: pageParams.pageTwoThird + 25,
      },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'left' },
      },
      startY,
      theme: 'plain',
      body: [
        ['Banking Details', ''],
        ['Company Name', 'Truda Food (Pty) Ltd'],
        ['Bank', 'First National Bank'],
        ['Account No.', '62070024759'],
        ['Branch Code', '221325'],
      ],
      didDrawPage: data => {
        sectionFourTableStart = data.cursor.y;
      },
    });

    autoTable(doc, {
      head: [['', '']],
      margin: {
        left: pageParams.pageOneThird + 10,
        right: pageParams.pageOneThird + 10,
      },
      columnStyles: {
        0: { halign: 'center' },
      },
      startY,
      theme: 'plain',
      body: [
        ['Message To Client'],
        ['_____________________________________'],
        ['_____________________________________'],
        ['_____________________________________'],
      ],
      willDrawCell: data => {
        switch (data.row.index) {
          case 0:
            doc.setFont(undefined, 'bold');
            break;
          default:
            doc.setFont(undefined, 'normal');
        }
      },
      didDrawPage: data => {
        sectionFourTableMiddle = data.cursor.y;
      },
    });

    // Create a table for totals with better styling
    autoTable(doc, {
      head: [['', '']],
      margin: {
        left: pageParams.pageTwoThird + 25,
        right: 10,
      },
      columnStyles: {
        0: { fontStyle: 'bold', halign: 'left' },
        1: { halign: 'right', fontStyle: 'normal' },
      },
      startY,
      theme: 'plain',
      body: [
        ['Total (Excl)', this.formatNumber(totals.runningTotalExcl)],
        ['Tax', this.formatNumber(totals.runningTotalTax)],
        ['Total (Incl)', this.formatNumber(totals.runningTotalIncl)],
      ],
      willDrawCell: data => {
        // Make the Total (Incl) row bold
        if (data.row.index === 2) {
          data.cell.styles.fontStyle = 'bold';
        }
      },
      didDrawPage: data => {
        sectionFourTableEnd = data.cursor.y;
      },
    });

    const sectionFourTablesEnd = this.getLongest([sectionFourTableStart, sectionFourTableMiddle, sectionFourTableEnd]);
    doc.setFont(undefined, 'bold');
    doc.text('Thank you for your continued business, it is greatly appreciated', pageParams.pageMiddle, sectionFourTablesEnd + 10, {
      align: 'center',
    });
    doc.setFont(undefined, 'normal');

    let signingTable = 0;
    autoTable(doc, {
      head: [['', '', '']],
      margin: {
        left: 25,
        right: 25,
      },
      columnStyles: {
        0: { halign: 'center' },
        1: { halign: 'center' },
        2: { halign: 'center' },
      },
      startY: sectionFourTablesEnd + 30,
      theme: 'plain',
      body: [
        [
          '__________________________________________',
          '__________________________________________',
          '__________________________________________',
        ],
        ['Received By', 'Signed', 'Date'],
      ],
      didDrawPage: data => {
        signingTable = data.cursor.y;
      },
    });

    this.drawHorizontalLine(signingTable + 5);
    const tableFooterStart = signingTable - 5;
    autoTable(doc, {
      head: [['', '', '']],
      margin: {
        left: 25,
        right: 25,
      },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold', cellWidth: 180 },
        1: { halign: 'center', fontStyle: 'bold', cellWidth: 180 },
        2: { halign: 'right', cellWidth: 180 },
      },
      startY: tableFooterStart,
      theme: 'plain',
      body: [
        [
          `Created Agent Name: ${agents.createdByAgent}`,
          `Modified Agent Name: ${agents.modifiedByAgent}`,
          moment().format('YYYY/MM/DD HH:mm:ss'),
        ],
      ],
    });
  }

  private addPageNumbers() {
    const { doc } = this;
    const totalPages = doc.getNumberOfPages();
    for (let i = 0; i < totalPages; i++) {
      doc.setPage(i);
      const pageCurrent = doc.getCurrentPageInfo().pageNumber; // Current Page
      doc.setTextColor(163, 168, 174);
      doc.setFontSize(8);
      doc.text(`Page ${pageCurrent} of ${totalPages}`, doc.internal.pageSize.width - 60, 15);
    }
  }

  formatNumber(num) {
    // Convert to number if it's a string
    const numValue = typeof num === 'string' ? parseFloat(num) : num;

    // Format with 2 decimal places and thousand separators
    return 'R ' + numValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // populateInvoiceSectionFive(doc: jsPDF, startY: number, pageParams: PDFPageParams, totals: GrandTotals, agents: AgentDetails) {
  //   this.drawHorizontalLine(doc, startY);
  //
  //   let sectionFourTableStart = 0;
  //   let sectionFourTableMiddle = 0;
  //   let sectionFourTableEnd = 0;
  //
  //   autoTable(doc, {
  //     head: [['', '']],
  //     margin: {
  //       left: 10,
  //       right: pageParams.pageTwoThird() + 25,
  //     },
  //     columnStyles: {
  //       0: { fontStyle: 'bold' },
  //       1: { halign: 'left' },
  //     },
  //     startY,
  //     theme: 'plain',
  //     body: [
  //       ['Banking Details', ''],
  //       ['Company Name', 'Truda Food (Pty) Ltd'],
  //       ['Bank', 'First National Bank'],
  //       ['Account No.', '62070024759'],
  //       ['Branch Code', '221325'],
  //     ],
  //     didDrawPage: data => {
  //       sectionFourTableStart = data.cursor.y;
  //     },
  //   });
  //
  //   autoTable(doc, {
  //     head: [['', '']],
  //     margin: {
  //       left: pageParams.pageOneThird() + 10,
  //       right: pageParams.pageOneThird() + 10,
  //     },
  //     columnStyles: {
  //       0: { halign: 'center' },
  //     },
  //     startY,
  //     theme: 'plain',
  //     body: [
  //       ['Message To Client'],
  //       ['_____________________________________'],
  //       ['_____________________________________'],
  //       ['_____________________________________'],
  //     ],
  //     willDrawCell: data => {
  //       switch (data.row.index) {
  //         case 0:
  //           doc.setFont(undefined, 'bold');
  //           break;
  //         default:
  //           doc.setFont(undefined, 'normal');
  //       }
  //     },
  //     didDrawPage: data => {
  //       sectionFourTableMiddle = data.cursor.y;
  //     },
  //   });
  //
  //   autoTable(doc, {
  //     head: [['', '']],
  //     margin: {
  //       left: pageParams.pageTwoThird() + 25,
  //       right: 10,
  //     },
  //     columnStyles: {
  //       1: { halign: 'right' },
  //     },
  //     startY,
  //     theme: 'plain',
  //     body: [
  //       ['Total (Excl)', this.formatNumber(totals.runningTotalExcl)],
  //       ['Tax', this.formatNumber(totals.runningTotalTax)],
  //       ['Total (Incl)', this.formatNumber(totals.runningTotalIncl)],
  //     ],
  //     willDrawCell: data => {
  //       switch (data.row.index) {
  //         case 2:
  //           doc.setFont(undefined, 'bold');
  //           break;
  //         default:
  //           doc.setFont(undefined, 'normal');
  //       }
  //     },
  //     didDrawPage: data => {
  //       sectionFourTableEnd = data.cursor.y;
  //     },
  //   });
  //
  //   const sectionFourTablesEnd = this.getLongest([sectionFourTableStart, sectionFourTableMiddle, sectionFourTableEnd]);
  //   doc.setFont(undefined, 'bold');
  //   doc.text('Thank you for your continued business, it is greatly appreciated', pageParams.pageMiddle(), sectionFourTablesEnd + 10, {
  //     align: 'center',
  //   });
  //   doc.setFont(undefined, 'normal');
  //
  //   let signingTable = 0;
  //   autoTable(doc, {
  //     head: [['', '', '']],
  //     margin: {
  //       left: 25,
  //       right: 25,
  //     },
  //     columnStyles: {
  //       0: { halign: 'center' },
  //       1: { halign: 'center' },
  //       2: { halign: 'center' },
  //     },
  //     startY: sectionFourTablesEnd + 30,
  //     theme: 'plain',
  //     body: [
  //       [
  //         '__________________________________________',
  //         '__________________________________________',
  //         '__________________________________________',
  //       ],
  //       ['Received By', 'Signed', 'Date'],
  //     ],
  //     didDrawPage: data => {
  //       signingTable = data.cursor.y;
  //     },
  //   });
  //
  //   this.drawHorizontalLine(doc, signingTable + 5);
  //   const tableFooterStart = signingTable - 5;
  //   autoTable(doc, {
  //     head: [['', '', '']],
  //     margin: {
  //       left: 25,
  //       right: 25,
  //     },
  //     columnStyles: {
  //       0: { halign: 'left', fontStyle: 'bold' },
  //       1: { halign: 'center', fontStyle: 'bold' },
  //       2: { halign: 'right' },
  //     },
  //     startY: tableFooterStart,
  //     theme: 'plain',
  //     body: [
  //       [
  //         `Created Agent Name: ${agents.createdByAgent}`,
  //         `Modified Agent Name: ${agents.modifiedByAgent}`,
  //         moment().format('YYYY/MM/DD HH:mm:ss'),
  //       ],
  //     ],
  //   });
  // }
}
